import { array, mixed, object, string, TestContext } from 'yup';
import {
    EntityAttributeDetailDto,
    GeckoStandardTestParameterDataType as DataType,
    GeckoStandardTestTypeDto as TestType
} from '@/api/backoffice/clients';

const integerParameterPattern = /^-?\d+$/;
const decimalParameterPattern = /^-?\d+([.,]\d+)?$/;

type TranslateFn = (key: string, values?: Record<string, unknown>) => string;

type StandardTestsComponentData = {
    standardTestTypes: TestType[];
};

type FormValues = {
    testType?: TestType;
    testParameters: {
        key: string;
        value: string | EntityAttributeDetailDto | null;
        label?: string | null;
    }[];
};

export const createValidationSchema = (t: TranslateFn, data: StandardTestsComponentData) => {
    const testParameterSchema = createTestParameterSchema(t, data);

    const validationSchema = object({
        testType: object().required(t('validations.messages.required', { _field_: t('gecko-test-form.testType') })),
        testParameters: array().of(testParameterSchema)
    });
    return validationSchema;
};

export const createTestParameterSchema = (t: TranslateFn, data: StandardTestsComponentData) => {
    return object().shape({
        key: string().required(),
        value: mixed()
            .nullable()
            .test('data-type-validation', function (value: unknown, context: TestContext) {
                const formValues = createFormValues(context);
                if (!formValues.testType) {
                    return true;
                }

                const standardTestType = data.standardTestTypes.find((x) => x.id === formValues.testType.id);
                if (!standardTestType) {
                    return true;
                }

                const parameterName = context.parent?.key;
                if (!parameterName) {
                    return true;
                }

                const field = context.parent?.label ?? parameterName;
                const dataType = getDataTypeForParameter(standardTestType, parameterName);
                const required = isRequiredParameter(standardTestType, parameterName);

                // AttributesList: value is an EntityAttributeDetailDto object
                if (dataType === DataType.AttributesList) {
                    if (required && !(value as EntityAttributeDetailDto)?.code) {
                        return context.createError({
                            message: t('validations.messages.required', { _field_: field })
                        });
                    }
                    return true;
                }

                const normalizedValue = (value as string | null | undefined)?.toString()?.trim();

                // check if the parameter is required and if a value is provided
                if (required && !normalizedValue) {
                    return context.createError({
                        message: t('validations.messages.required', { _field_: field })
                    });
                }

                // check if the value is valid for the data type of the parameter
                if (isValidParameterValueForDataType(normalizedValue, dataType)) {
                    return true;
                }

                if (dataType === DataType.Integer) {
                    return context.createError({
                        message: t('validations.messages.mustBeInteger', { _field_: field })
                    });
                }

                if (dataType === DataType.Decimal) {
                    return context.createError({
                        message: t('validations.messages.mustBeNumber', { _field_: field })
                    });
                }

                return true;
            }),
        label: string().nullable(),
        dataType: string().nullable()
    });
};

const getDataTypeForParameter = (testType: TestType, parameterName: string): DataType => {
    const parameterType = testType?.parameters?.find((p) => p.name === parameterName);
    if (!parameterType || parameterType.dataType === null || parameterType.dataType === undefined) {
        return DataType.String;
    }
    return parameterType.dataType;
};

const isRequiredParameter = (testType: TestType, parameterName: string): boolean => {
    const parameterType = testType.parameters?.find((p) => p.name === parameterName);
    return !!parameterType?.required;
};

const isValidParameterValueForDataType = (normalizedValue: string | null | undefined, dataType: DataType) => {
    if (!normalizedValue) {
        return true;
    }

    switch (dataType) {
        case DataType.Integer:
            return integerParameterPattern.test(normalizedValue);
        case DataType.Decimal:
            return decimalParameterPattern.test(normalizedValue);
        case DataType.String:
        default:
            return true;
    }
};

const createFormValues = (context: TestContext): FormValues => {
    return (context.options.context ?? {}) as FormValues;
};
